// src/pages/BigDatabasePage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { doc, getDoc } from "firebase/firestore";
import Header from "../components/Header";
import { db } from "../data/firebase";
import "./BigDatabasePage.css";

function parseAllOld(raw) {
  // Case A: already normalized array of { name, info[] }
  if (Array.isArray(raw)) {
    // If it's an array of strings, treat as CSV lines below
    const looksLikeStrings = raw.every((x) => typeof x === "string");
    if (!looksLikeStrings) {
      return raw
        .map((item) => {
          const name =
            item && typeof item.name === "string" ? item.name.trim() : "";
          const infoArr = Array.isArray(item?.info)
            ? item.info.map((s) => String(s ?? "").trim()).filter(Boolean)
            : [];
          if (!name && infoArr.length === 0) return null;
          return { name, info: infoArr };
        })
        .filter(Boolean);
    }
    // Fall through to parse array-of-strings as CSV lines
    raw = raw.join("\n");
  }

  // Case B: CSV text (string) — format: "Name, first line\n, next line\nName2, ..."
  if (typeof raw === "string") {
    const lines = raw.split(/\r?\n/);
    const parsed = [];
    let current = null;

    for (let rawLine of lines) {
      const line = String(rawLine ?? "");
      if (!line) continue;

      const commaIdx = line.indexOf(",");
      if (commaIdx === -1) continue;

      const col1 = line.slice(0, commaIdx).trim();
      const col2 = line.slice(commaIdx + 1).trim();

      if (col1) {
        current = { name: col1, info: [] };
        parsed.push(current);
        if (col2) current.info.push(col2);
      } else if (current && col2) {
        current.info.push(col2);
      }
    }
    return parsed;
  }

  // Unknown format → return empty
  return [];
}

export default function BigDatabasePage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 50;

  useEffect(() => {
    async function loadFromFirestore() {
      try {
        const ref = doc(db, "Big records", "BigRecords");
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          throw new Error("Document 'BigRecords' not found");
        }
        const data = snap.data() || {};
        const raw = data["ALL-OLD"];

        const parsed = parseAllOld(raw);
        setRecords(parsed);
      } catch (e) {
        console.error("Error loading BigRecords:", e);
        setError(e?.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    }
    loadFromFirestore();
  }, []);

  // Always search all fields
  const filtered = useMemo(() => {
    if (!query.trim()) return records;
    const q = query.toLowerCase();
    return records.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.info || []).some((line) => (line || "").toLowerCase().includes(q))
    );
  }, [records, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageRecords = filtered.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE
  );

  const goToPage = (n) => setCurrentPage(Math.min(Math.max(1, n), totalPages));

  const handleNavigate = (route) => {
    if (route !== "big") window.location.assign("/");
  };

  return (
    <div className="app">
      <Header
        currentRoute="big"
        onNavigate={handleNavigate}
        showSearch={true}
        query={query}
        onQueryChange={(q) => {
          setQuery(q);
          setCurrentPage(1);
        }}
        searchField="all"
        onSearchFieldChange={() => {}}
        searchOptions={[{ value: "all", label: "All fields" }]}
      />

      <main className="container">
        {loading ? (
          <div className="bigdb__loading">Loading data…</div>
        ) : error ? (
          <div className="bigdb__loading">Error: {error}</div>
        ) : filtered.length === 0 ? (
          <div className="bigdb__loading">No entries found</div>
        ) : (
          <>
            <div className="bigdb__grid">
              {pageRecords.map((rec, idx) => (
                <article key={idx} className="product-card-box bigdb__card">
                  <div className="product-card-box__head">
                    <h3 className="product-card-box__name bigdb__name">
                      {rec.name}
                    </h3>
                  </div>
                  {!!(rec.info && rec.info.length) && (
                    <div className="bigdb__info">
                      {rec.info.map((line, j) => (
                        <p key={j} className="bigdb__info-line">
                          {line}
                        </p>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>

            <div className="bigdb__pagination">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
